import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Sparkles, Gift, Heart, ArrowRight, ArrowLeft, RotateCcw, ChevronRight, Check, 
  Clock, Search, Star, Package, X
} from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Header from '../components/Header';
import MainFooter from '../components/MainFooter';
import { aiGenerationsService } from '../services/aiGenerationsService';
import { guestLimitService } from '../services/guestLimitService';
import API_URL from '../config/api';

// Import relationship images
import momImage from '../assets/relations/mom.jpg';
import dadImage from '../assets/relations/dad.jpg';
import sisterImage from '../assets/relations/sister.jpg';
import brotherImage from '../assets/relations/brother.jpg';
import wifeImage from '../assets/relations/mom.jpg';
import husbandImage from '../assets/relations/dad.jpg';
import boyfriendImage from '../assets/relations/boyfriend.jpg';
import girlfriendImage from '../assets/relations/girlfriend.jpg';
import bestFriendImage from '../assets/relations/bestfriend.jpg';
import couplesImage from '../assets/relations/couples.jpg';
import grandmaImage from '../assets/relations/grandma.jpg';
import grandpaImage from '../assets/relations/grandpa.jpg';
import sonImage from '../assets/relations/son.jpg';
import daughterImage from '../assets/relations/daughter.jpg';

// Import occasion images
import birthdayImage from '../assets/occasions/birthday.png';
import christmasImage from '../assets/occasions/christmas.png';
import anniversaryImage from '../assets/occasions/anniversary.png';
import valentinesImage from '../assets/occasions/valentines.png';
import mothersDayImage from '../assets/occasions/mothersday.png';
import fathersDayImage from '../assets/occasions/fathersday.png';
import weddingImage from '../assets/occasions/wedding.png';
import justBecauseImage from '../assets/occasions/justbecause.png';
import housewarmingImage from '../assets/occasions/housewarming.png';
import babyShowerImage from '../assets/occasions/babyshower.png';
import thanksgivingImage from '../assets/occasions/thanksgiving.png';

// Import age group images
import kidsImage from '../assets/agegroups/kids.png';
import teensImage from '../assets/agegroups/teens.jpg';
import adultsImage from '../assets/agegroups/adults.jpg';
import seniorsImage from '../assets/agegroups/seniors.jpg';
import allAgesImage from '../assets/agegroups/allages.jpg';

// Import personality images
import creativeImage from '../assets/personalities/creative.png';
import artisticImage from '../assets/personalities/artistic.png';
import techSavvyImage from '../assets/personalities/techsavvy.png';
import adventurousImage from '../assets/personalities/adventurous.png';
import funnyImage from '../assets/personalities/funny.png';
import luxuryImage from '../assets/personalities/luxury.png';
import gamerImage from '../assets/personalities/gamer.png';
import foodieImage from '../assets/personalities/foodie.png';
import natureLoverImage from '../assets/personalities/naturelover.png';
import bookwormImage from '../assets/personalities/bookworm.png';
import fitnessImage from '../assets/personalities/fitness.png';

// Fallback animation component
const FallbackLoadingAnimation = () => (
  <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto relative">
    <div className="absolute inset-0 bg-[#06402B]/5 rounded-full animate-ping" />
    <div className="absolute inset-4 bg-[#06402B]/10 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
    <div className="absolute inset-8 bg-[#06402B]/15 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
    
    <div className="relative w-full h-full bg-white rounded-2xl flex items-center justify-center shadow-2xl animate-bounce">
      <Gift className="w-12 h-12 sm:w-16 sm:h-16 text-[#06402B]" strokeWidth={1.5} />
    </div>
    
    <Sparkles className="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 text-[#06402B] animate-pulse" />
    <Sparkles className="absolute -bottom-2 -left-2 w-4 h-4 sm:w-6 sm:h-6 text-[#06402B] animate-pulse" style={{ animationDelay: '0.3s' }} />
    <Sparkles className="absolute top-1/2 -right-4 w-4 h-4 sm:w-5 sm:h-5 text-[#06402B] animate-pulse" style={{ animationDelay: '0.6s' }} />
  </div>
);

// Lottie animation component
const LottieAnimationContent = React.memo(({ onError }) => {
  return (
    <div className="relative w-40 h-40 sm:w-56 sm:h-56 md:w-64 md:h-64 mx-auto">
      <DotLottieReact
        src="https://lottie.host/192247e1-c593-476c-93bb-9ec6cedd3f42/yynondVPht.lottie"
        loop
        autoplay
        onError={onError}
        style={{ width: '100%', height: '100%' }}
      />
      <Sparkles className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 text-[#06402B] animate-pulse" />
      <Sparkles className="absolute -bottom-2 -left-2 w-4 h-4 sm:w-5 sm:h-5 text-[#06402B] animate-pulse" style={{ animationDelay: '0.3s' }} />
      <Sparkles className="absolute top-1/2 -right-4 w-3 h-3 sm:w-4 sm:h-4 text-[#06402B] animate-pulse" style={{ animationDelay: '0.6s' }} />
      <Sparkles className="absolute top-1/4 -left-4 w-3 h-3 sm:w-4 sm:h-4 text-[#06402B] animate-pulse" style={{ animationDelay: '0.9s' }} />
    </div>
  );
});

LottieAnimationContent.displayName = 'LottieAnimationContent';

const EnhancedLoadingAnimation = React.memo(({
  user,
  onLogout,
  currentScreen,
  onNavigate,
  animationComponent,
  loadingProgress,
  selectedTags
}) => {
  return (
    <div className="fixed inset-0 z-40 bg-[#FBFCF6]/95 backdrop-blur-sm">
      <div className="absolute top-0 left-0 right-0 z-50">
        <Header
          user={user}
          onLogout={onLogout}
          currentScreen={currentScreen}
          onNavigate={onNavigate}
        />
      </div>

      <div className="flex flex-col items-center justify-center min-h-screen pt-16 sm:pt-20 px-4">
        <div className="mb-4 sm:mb-6">
          {animationComponent}
        </div>

        <span className="text-4xl sm:text-5xl font-bold text-[#06402B]">
          {Math.round(loadingProgress)}%
        </span>

        <h4 className="text-sm sm:text-base md:text-3xl font-bold text-slate-800 mb-2 sm:mb-3 animate-fade-in text-center px-4">
          {loadingProgress < 25 
            ? 'Understanding your preferences...'
            : loadingProgress < 50 
            ? 'Exploring gift options...'
            : loadingProgress < 75 
            ? 'Finding perfect matches...'
            : 'Almost ready...'}
        </h4>

        <p className="text-slate-600 mt-1 sm:mt-2 text-xs sm:text-sm md:text-base text-center">
          Our AI is curating personalized recommendations
        </p>

        <div className="w-full max-w-xs sm:max-w-md h-2 bg-slate-200 rounded-full mt-4 overflow-hidden">
          <div
            className="h-full bg-[#06402B] transition-all duration-300"
            style={{ width: `${loadingProgress}%` }}
          />
        </div>

        <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mt-4 sm:mt-6 px-4">
          {selectedTags.map((tag, i) => (
            <span
              key={i}
              className="px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs bg-[#06402B]/10 text-[#06402B] rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
});

EnhancedLoadingAnimation.displayName = 'EnhancedLoadingAnimation';

const AIFinderScreen = ({ 
  user, 
  onLogout, 
  currentScreen, 
  onNavigate, 
  onProductSelect, 
  wishlist, 
  toggleWishlist,
  navigationData,
  previousScreen
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  const [selectedRelationships, setSelectedRelationships] = useState([]);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
  const [selectedPersonality, setSelectedPersonality] = useState([]);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [generationHistory, setGenerationHistory] = useState([]);
  const [lottieError, setLottieError] = useState(false);
  
  // GUEST MODE STATE
  const [guestTriesRemaining, setGuestTriesRemaining] = useState(4);
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
  
  const [cameFrom, setCameFrom] = useState(previousScreen || 'home');
  
  const progressInterval = useRef(null);
  const resultsRef = useRef(null);

  // Check guest limits on mount
  useEffect(() => {
    if (!user) {
      const stats = guestLimitService.getUsageStats();
      setGuestTriesRemaining(stats.remaining);
      console.log('Guest AI Finder stats:', stats);
    }
  }, [user]);

  // Update tries remaining when user logs in
  useEffect(() => {
    if (user) {
      setGuestTriesRemaining(0); // Unlimited for logged-in users
      setShowGuestLimitModal(false);
    }
  }, [user]);

  // Handle incoming generation data
  useEffect(() => {
    if (navigationData?.generation) {
      const gen = navigationData.generation;
      console.log('📦 Loading generation from navigation:', gen);
      
      setSelectedOccasions(gen.occasions || []);
      setSelectedRelationships(gen.relationships || []);
      setSelectedAgeGroups(gen.age_groups || []);
      setSelectedPersonality(gen.personality || []);
      setQuery('');
      
      setResults(gen.results || []);
      setShowResults(true);
      setCurrentStep(0);
      
      setCameFrom(previousScreen || 'profile');
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [navigationData, previousScreen]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };
  }, []);

  // Load generation history
  const loadGenerationHistory = useCallback(async () => {
    if (!user) return;
    
    try {
      const data = await aiGenerationsService.getGenerations(user.id, 10);
      setGenerationHistory(data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadGenerationHistory();
    }
  }, [user, loadGenerationHistory]);

  const saveGeneration = useCallback(async (searchData, resultsData) => {
    if (!user) return;

    try {
      const saved = await aiGenerationsService.saveGeneration(user.id, searchData, resultsData);
      if (saved) {
        await loadGenerationHistory();
      }
    } catch (error) {
      console.error('Error saving generation:', error);
    }
  }, [user, loadGenerationHistory]);

  const loadPreviousGeneration = useCallback((generation) => {
    setSelectedOccasions(generation.occasions || []);
    setSelectedRelationships(generation.relationships || []);
    setSelectedAgeGroups(generation.age_groups || []);
    setSelectedPersonality(generation.personality || []);
    setQuery('');
    setResults(generation.results || []);
    setShowResults(true);
    setCurrentStep(0);
    setCameFrom('aiFinder');
    
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const relationshipGenderMap = useMemo(() => ({
    'Mom': 'Female',
    'Sister': 'Female',
    'Wife': 'Female',
    'Girlfriend': 'Female',
    'Grandma': 'Female',
    'Daughter': 'Female',
    'Dad': 'Male',
    'Brother': 'Male',
    'Husband': 'Male',
    'Boyfriend': 'Male',
    'Grandpa': 'Male',
    'Son': 'Male',
    'Best Friend': 'Unisex',
    'Couples': 'Unisex'
  }), []);

  const personalityKeywords = useMemo(() => ({
    'Creative': ['creative', 'art', 'craft'],
    'Artistic': ['art', 'drawing', 'painting', 'decor'],
    'Tech-Savvy': ['tech', 'gadget', 'electronic'],
    'Adventurous': ['outdoor', 'adventure', 'travel'],
    'Funny': ['funny', 'humor', 'game', 'fun'],
    'Luxury-Loving': ['luxury', 'premium', 'spa'],
    'Gamer': ['game', 'gaming', 'video game'],
    'Foodie': ['food', 'cooking', 'kitchen', 'gourmet', 'snack'],
    'Nature Lover': ['nature', 'plant', 'garden', 'flower', 'botanical'],
    'Bookworm': ['book', 'reading', 'literary'],
    'Fitness Enthusiast': ['fitness', 'sport', 'workout', 'gym']
  }), []);

  const ageGroupKeywords = useMemo(() => ({
    'Kids': ['kids', 'children', 'toy', 'toys'],
    'Teens': ['teen', 'teenager', 'cool'],
    'Adults': ['adult', 'professional'],
    'Seniors': ['senior', 'classic'],
    'All Ages': []
  }), []);

  const getInferredGenders = useCallback(() => {
    const genders = new Set();
    selectedRelationships.forEach(rel => {
      const gender = relationshipGenderMap[rel];
      if (gender) {
        if (gender === 'Unisex') {
          genders.add('Male');
          genders.add('Female');
        } else {
          genders.add(gender);
        }
      }
    });
    return Array.from(genders);
  }, [selectedRelationships, relationshipGenderMap]);

  const buildSmartQuery = useCallback(() => {
    const queryParts = [];
    
    if (selectedRelationships.length > 0) {
      queryParts.push(`gift for ${selectedRelationships[0].toLowerCase()}`);
    }
    
    if (selectedAgeGroups.length > 0) {
      const ageKeywords = ageGroupKeywords[selectedAgeGroups[0]] || [];
      if (ageKeywords.length > 0) {
        queryParts.push(ageKeywords.slice(0, 2).join(' '));
      }
    }
    
    if (selectedPersonality.length > 0) {
      const interestKeywords = [];
      selectedPersonality.forEach(pers => {
        const keywords = personalityKeywords[pers] || [];
        if (keywords.length > 0) {
          interestKeywords.push(keywords[0]);
        }
      });
      if (interestKeywords.length > 0) {
        queryParts.push(`who loves ${interestKeywords.join(' and ')}`);
      }
    }
    
    if (selectedOccasions.length > 0) {
      queryParts.push(`for ${selectedOccasions[0]}`);
    }
    
    let finalQuery = queryParts.join(' ');
    if (query.trim()) {
      finalQuery = `${finalQuery}. ${query.trim()}`;
    }
    
    return finalQuery || 'Find me the perfect gift';
  }, [selectedRelationships, selectedAgeGroups, selectedPersonality, selectedOccasions, query, ageGroupKeywords, personalityKeywords]);

  const steps = useMemo(() => [
    { id: 'occasion', title: 'What\'s the Occasion?', subtitle: 'Select the special moment' },
    { id: 'relationship', title: 'Who is it for?', subtitle: 'Tell us about the recipient' },
    { id: 'age', title: 'Age Group', subtitle: 'Help us narrow down' },
    { id: 'personality', title: 'Their Personality', subtitle: 'What are they like?' },
    { id: 'details', title: 'Any Details?', subtitle: 'Optional extra context' }
  ], []);

  const occasions = useMemo(() => [
    { label: 'Birthday', image: birthdayImage },
    { label: 'Christmas', image: christmasImage },
    { label: 'Anniversary', image: anniversaryImage },
    { label: "Valentine's Day", image: valentinesImage },
    { label: "Mother's Day", image: mothersDayImage },
    { label: "Father's Day", image: fathersDayImage },
    { label: 'Wedding', image: weddingImage },
    { label: 'Just Because', image: justBecauseImage },
    { label: 'Housewarming', image: housewarmingImage },
    { label: 'Baby Shower', image: babyShowerImage },
    { label: 'Thanksgiving', image: thanksgivingImage }
  ], []);

  const relationships = useMemo(() => [
    { label: 'Mom', gender: 'Female', image: momImage },
    { label: 'Dad', gender: 'Male', image: dadImage },
    { label: 'Sister', gender: 'Female', image: sisterImage },
    { label: 'Brother', gender: 'Male', image: brotherImage },
    { label: 'Husband', gender: 'Male', image: husbandImage },
    { label: 'Wife', gender: 'Female', image: wifeImage },
    { label: 'Boyfriend', gender: 'Male', image: boyfriendImage },
    { label: 'Girlfriend', gender: 'Female', image: girlfriendImage },
    { label: 'Best Friend', gender: 'Unisex', image: bestFriendImage },
    { label: 'Couples', gender: 'Unisex', image: couplesImage },
    { label: 'Grandma', gender: 'Female', image: grandmaImage },
    { label: 'Grandpa', gender: 'Male', image: grandpaImage },
    { label: 'Son', gender: 'Male', image: sonImage },
    { label: 'Daughter', gender: 'Female', image: daughterImage }
  ], []);

  const ageGroups = useMemo(() => [
    { label: 'Kids', image: kidsImage, desc: '0-12 years' },
    { label: 'Teens', image: teensImage, desc: '13-19 years' },
    { label: 'Adults', image: adultsImage, desc: '20-55 years' },
    { label: 'Seniors', image: seniorsImage, desc: '55+ years' },
    { label: 'All Ages', image: allAgesImage, desc: 'Universal' }
  ], []);

  const personalities = useMemo(() => [
    { label: 'Creative', image: creativeImage },
    { label: 'Artistic', image: artisticImage },
    { label: 'Tech-Savvy', image: techSavvyImage },
    { label: 'Adventurous', image: adventurousImage },
    { label: 'Funny', image: funnyImage },
    { label: 'Luxury-Loving', image: luxuryImage },
    { label: 'Gamer', image: gamerImage },
    { label: 'Foodie', image: foodieImage },
    { label: 'Nature Lover', image: natureLoverImage },
    { label: 'Bookworm', image: bookwormImage },
    { label: 'Fitness Enthusiast', image: fitnessImage }
  ], []);

  const toggleSelection = useCallback((array, setArray, item) => {
    if (array.includes(item)) {
      setArray(array.filter(i => i !== item));
    } else {
      setArray([...array, item]);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!selectedOccasions.length && !selectedRelationships.length) {
      return;
    }

    // GUEST LIMIT CHECK
    if (!user) {
      const canUse = guestLimitService.canUseAIFinder();
      if (!canUse) {
        setShowGuestLimitModal(true);
        return;
      }
    }

    setIsGenerating(true);
    setLoadingProgress(0);
    setShowResults(false);
    setLottieError(false);

    window.scrollTo({ top: 0, behavior: 'smooth' });

    const inferredGenders = getInferredGenders();
    const smartQuery = buildSmartQuery();

    const totalDuration = 30000;
    const intervalTime = 100;
    const incrementPerInterval = 100 / (totalDuration / intervalTime);
    
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
    
    progressInterval.current = setInterval(() => {
      setLoadingProgress(prev => {
        const next = prev + incrementPerInterval;
        if (next >= 99) {
          return 99;
        }
        return next;
      });
    }, intervalTime);

    const searchData = {
      query: smartQuery,
      occasions: selectedOccasions,
      gender: inferredGenders,
      relationships: selectedRelationships,
      age_groups: selectedAgeGroups,
      personality: selectedPersonality
    };

    try {
      const response = await fetch(`${API_URL}/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...searchData,
          top_k: 25,
          use_ai: true
        })
      });

      if (!response.ok) throw new Error('Failed to generate');

      const data = await response.json();
      
      // INCREMENT GUEST USAGE AFTER SUCCESSFUL GENERATION
      if (!user) {
        // const newCount = guestLimitService.incrementUsage();
        const remaining = guestLimitService.getRemainingTries();
        setGuestTriesRemaining(remaining);
        console.log(`Guest used AI Finder. Tries remaining: ${remaining}`);
        
        // Show reminder if running low
        if (remaining === 1) {
          setTimeout(() => {
            alert('This is your last free AI search! Sign in to get unlimited searches.');
          }, 2000);
        } else if (remaining === 0) {
          setTimeout(() => {
            setShowGuestLimitModal(true);
          }, 2000);
        }
      }
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
      
      setLoadingProgress(100);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setIsGenerating(false);
      setLoadingProgress(0);
      setResults(data);
      setShowResults(true);
      setCameFrom('aiFinder');
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      
      saveGeneration(searchData, data);
      
    } catch (error) {
      console.error('Generation error:', error);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
      setIsGenerating(false);
      setLoadingProgress(0);
      alert('Failed to generate recommendations. Please try again.');
    }
  }, [selectedOccasions, selectedRelationships, selectedAgeGroups, selectedPersonality, getInferredGenders, buildSmartQuery, saveGeneration, user]);

  const handleReset = useCallback(() => {
    setCurrentStep(0);
    setQuery('');
    setSelectedOccasions([]);
    setSelectedRelationships([]);
    setSelectedAgeGroups([]);
    setSelectedPersonality([]);
    setResults([]);
    setShowResults(false);
    setCameFrom('aiFinder');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 0: return selectedOccasions.length > 0;
      case 1: return selectedRelationships.length > 0;
      case 2: return true;
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  }, [currentStep, selectedOccasions.length, selectedRelationships.length]);

  // Enhanced Image Card
  const ImageCard = useCallback(({ item, selected, onClick, showGender = false, showDesc = false }) => {
    return (
      <button
        onClick={onClick}
        type="button"
        className={`group relative p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 transform active:scale-95 ${
          selected
            ? 'border-[#06402B] bg-[#06402B]/5 shadow-lg'
            : 'border-slate-200 bg-white hover:border-[#06402B]/30 hover:shadow-md'
        }`}
      >
        {selected && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10">
            <Check className="w-4 h-4 sm:w-6 sm:h-6 text-[#06402B] drop-shadow-md" strokeWidth={2.5} />
          </div>
        )}
        
        <div className="flex flex-col items-center text-center gap-2 sm:gap-3">
          <div className={`relative w-14 h-14 sm:w-20 sm:h-20 rounded-full overflow-hidden transition-all duration-300 border-2 sm:border-4 ${
            selected ? 'scale-105 sm:scale-110 ring-2 sm:ring-4 ring-transparent border-transparent' : 'border-slate-100'
          }`}>
            <img 
              src={item.image} 
              alt={item.label}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          
          <div className="space-y-0.5 sm:space-y-1">
            <span className={`font-semibold text-xs sm:text-sm transition-colors block leading-tight ${
              selected ? 'text-[#06402B]' : 'text-slate-700'
            }`}>
              {item.label}
            </span>
            {showDesc && item.desc && (
              <span className="text-[10px] sm:text-xs text-slate-500 block">{item.desc}</span>
            )}
            {showGender && item.gender && (
              <span className={`inline-block text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${
                item.gender === 'Female' 
                  ? 'bg-pink-100 text-pink-700' 
                  : item.gender === 'Male' 
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {item.gender}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  }, []);

  // Step Indicator
  const StepIndicator = useCallback(() => (
    <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 sm:mb-8 px-2">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <button
            onClick={() => index < currentStep && setCurrentStep(index)}
            disabled={index > currentStep}
            type="button"
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-all duration-300 ${
              index < currentStep
                ? 'bg-[#06402B] text-white cursor-pointer hover:bg-[#053527]'
                : index === currentStep
                ? 'bg-[#06402B] text-white ring-2 sm:ring-4 ring-[#06402B]/20'
                : 'bg-slate-100 text-slate-400'
            }`}
          >
            {index < currentStep ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : index + 1}
          </button>
          {index < steps.length - 1 && (
            <div className={`w-4 sm:w-8 md:w-12 h-0.5 sm:h-1 rounded-full transition-colors duration-300 ${
              index < currentStep ? 'bg-[#06402B]' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  ), [steps, currentStep]);

  const handleLottieError = useCallback(() => {
    setLottieError(true);
  }, []);

  const AnimationComponent = useMemo(() => {
    if (lottieError) {
      return (
        <div className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto">
          <FallbackLoadingAnimation />
        </div>
      );
    }
    return <LottieAnimationContent onError={handleLottieError} />;
  }, [lottieError, handleLottieError]);

  // Enhanced Product Card with pricing and complete information
  const ProductCard = useCallback(({ product }) => {
    const hasDiscount = product.savings_percentage && product.savings_percentage > 0;
    
    return (
      <div className="group bg-white rounded-xl sm:rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-[#06402B]/20 transition-all duration-300">
        <div className="relative aspect-square overflow-hidden bg-slate-50">
          <img 
            src={product.image_urls?.[0]}
            alt={product.title} 
            className="w-full h-full object-contain p-4 sm:p-6 group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          
          {/* Wishlist button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleWishlist(product.asin);
            }}
            type="button"
            className={`absolute top-2 right-2 sm:top-3 sm:right-3 w-9 h-9 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center shadow-md transition-all active:scale-95 ${
              wishlist.includes(product.asin) ? 'opacity-100' : 'md:opacity-0 md:group-hover:opacity-100'
            }`}
          >
            <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${
              wishlist.includes(product.asin) ? 'fill-red-500 text-red-500' : 'text-slate-400'
            }`} />
          </button>

          {/* Discount badge */}
          {hasDiscount && (
            <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
              <div className="px-2 sm:px-2.5 py-1 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full">
                {Math.round(product.savings_percentage)}% OFF
              </div>
            </div>
          )}
        </div>
        
        <div className="p-3 sm:p-5 bg-white relative">
          {/* Brand and rating */}
          <div className="flex items-center justify-between mb-2">
            {product.brand && (
              <span className="text-[10px] sm:text-xs text-slate-500 font-medium truncate flex-1">
                {product.brand}
              </span>
            )}
            {product.star_rating && (
              <div className="flex items-center gap-1 ml-2">
                <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-yellow-400 text-yellow-400" />
                <span className="text-[10px] sm:text-xs font-semibold text-slate-700">
                  {product.star_rating}
                </span>
                {product.review_count && (
                  <span className="text-[10px] sm:text-xs text-slate-400">
                    ({product.review_count.toLocaleString()})
                  </span>
                )}
              </div>
            )}
          </div>

          <h3 className="font-bold text-slate-800 line-clamp-2 mb-2 sm:mb-3 min-h-[2.5rem] sm:min-h-[3rem] text-sm sm:text-base">
            {product.short_title || product.title}
          </h3>
          
          {/* Price section */}
          <div className="mb-3 sm:mb-4">
            <div className="flex items-baseline gap-2 flex-wrap">
              {product.price_display && (
                <span className="text-lg sm:text-xl font-bold text-[#06402B]">
                  {product.price_display}
                </span>
              )}
              {hasDiscount && product.list_price && (
                <span className="text-xs sm:text-sm text-slate-400 line-through">
                  ${parseFloat(product.list_price).toFixed(2)}
                </span>
              )}
              {product.savings_amount && (
                <span className="text-[10px] sm:text-xs text-green-600 font-semibold">
                  Save ${parseFloat(product.savings_amount).toFixed(2)}
                </span>
              )}
            </div>
            {product.availability && (
              <div className="flex items-center gap-1 mt-1">
                <Package className="w-3 h-3 text-green-600" />
                <span className="text-[10px] sm:text-xs text-green-600 font-medium">
                  {product.availability}
                </span>
              </div>
            )}
          </div>

          {/* Match scores visualization */}
          {product.match_scores && (
            <div className="mb-3 sm:mb-4">
              <div className="grid grid-cols-2 gap-1 sm:gap-1.5">
                {product.match_scores.occasion > 0 && (
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                    <div className="w-2 h-2 rounded-full bg-[#06402B]" />
                    <span className="text-slate-600">Occasion {(product.match_scores.occasion * 100).toFixed(0)}%</span>
                  </div>
                )}
                {product.match_scores.relationship > 0 && (
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-slate-600">Recipient {(product.match_scores.relationship * 100).toFixed(0)}%</span>
                  </div>
                )}
                {product.match_scores.personality > 0 && (
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                    <div className="w-2 h-2 rounded-full bg-purple-500" />
                    <span className="text-slate-600">Interest {(product.match_scores.personality * 100).toFixed(0)}%</span>
                  </div>
                )}
                {product.match_scores.age_group > 0 && (
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-slate-600">Age {(product.match_scores.age_group * 100).toFixed(0)}%</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <button
            onClick={() => onProductSelect(product)}
            type="button"
            className="w-full bg-[#06402B] text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-[#053527] transition-colors font-semibold flex items-center justify-center gap-2 text-sm sm:text-base active:scale-95"
          >
            View Details
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }, [wishlist, toggleWishlist, onProductSelect]);

  // Guest Limit Modal Component
  const GuestLimitModal = useCallback(() => {
    if (!showGuestLimitModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowGuestLimitModal(false)}
        />
        
        <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl p-6 sm:p-8 animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200">
          <div className="sm:hidden w-12 h-1.5 bg-slate-300 rounded-full mx-auto mb-6" />
          
          <button
            onClick={() => setShowGuestLimitModal(false)}
            type="button"
            className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-red-600" />
          </div>

          <h3 className="text-xl sm:text-2xl font-bold text-slate-800 text-center mb-2">
            Free Tries Used Up
          </h3>

          <p className="text-slate-600 text-center mb-6 text-sm sm:text-base">
            You've used all 4 free AI gift searches. Sign in to get unlimited personalized recommendations!
          </p>

          <div className="bg-[#FBFCF6] rounded-xl p-4 mb-6 space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-[#06402B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-slate-700">Unlimited AI gift searches</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-[#06402B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-slate-700">Save search history</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-[#06402B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-slate-700">Create wishlists</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-[#06402B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-slate-700">Personalized recommendations</p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setShowGuestLimitModal(false);
                onNavigate('login');
              }}
              type="button"
              className="w-full px-6 py-3.5 bg-[#06402B] text-white rounded-xl hover:bg-[#053527] transition-colors font-semibold text-base shadow-lg active:scale-95"
            >
              Sign In
            </button>
            
            <button
              onClick={() => {
                setShowGuestLimitModal(false);
                onNavigate('register');
              }}
              type="button"
              className="w-full px-6 py-3.5 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-semibold text-base active:scale-95"
            >
              Create Free Account
            </button>
          </div>

          <button
            onClick={() => setShowGuestLimitModal(false)}
            type="button"
            className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            Continue browsing products
          </button>
        </div>
      </div>
    );
  }, [showGuestLimitModal, onNavigate]);

  const renderStepContent = useCallback(() => {
    switch (currentStep) {
      case 0:
        return (
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {occasions.map(item => (
              <ImageCard
                key={item.label}
                item={item}
                selected={selectedOccasions.includes(item.label)}
                onClick={() => toggleSelection(selectedOccasions, setSelectedOccasions, item.label)}
              />
            ))}
          </div>
        );
      case 1:
        return (
          <div>
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
              {relationships.map(item => (
                <ImageCard
                  key={item.label}
                  item={item}
                  selected={selectedRelationships.includes(item.label)}
                  onClick={() => toggleSelection(selectedRelationships, setSelectedRelationships, item.label)}
                />
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4 max-w-2xl mx-auto">
            {ageGroups.map(item => (
              <ImageCard
                key={item.label}
                item={item}
                selected={selectedAgeGroups.includes(item.label)}
                onClick={() => toggleSelection(selectedAgeGroups, setSelectedAgeGroups, item.label)}
                showDesc
              />
            ))}
          </div>
        );
      case 3:
        return (
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {personalities.map(item => (
              <ImageCard
                key={item.label}
                item={item}
                selected={selectedPersonality.includes(item.label)}
                onClick={() => toggleSelection(selectedPersonality, setSelectedPersonality, item.label)}
              />
            ))}
          </div>
        );
      case 4:
        return (
          <div className="max-w-xl mx-auto px-2">
            <div className="mb-4 p-3 sm:p-4 bg-[#06402B]/5 rounded-xl border border-[#06402B]/10">
              <p className="text-[10px] sm:text-xs font-semibold text-[#06402B] mb-1.5 sm:mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                AI Search Query Preview:
              </p>
              <p className="text-xs sm:text-sm text-slate-700 italic">"{buildSmartQuery()}"</p>
            </div>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Add more details... E.g., He loves building LEGO sets and playing outdoor games..."
              className="w-full px-4 sm:px-5 py-3 sm:py-4 border-2 border-slate-200 rounded-xl sm:rounded-2xl focus:border-[#06402B] focus:ring-4 focus:ring-[#06402B]/10 outline-none resize-none h-28 sm:h-32 text-slate-700 text-sm sm:text-lg"
            />
            <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-slate-500 text-center">
              The more details you provide, the better our recommendations will be
            </p>
          </div>
        );
      default:
        return null;
    }
  }, [currentStep, occasions, relationships, ageGroups, personalities, selectedOccasions, selectedRelationships, selectedAgeGroups, selectedPersonality, query, toggleSelection, buildSmartQuery]);

  return (
    <div className="min-h-screen bg-[#FBFCF6]">
      <Header 
        user={user} 
        onLogout={onLogout} 
        currentScreen={currentScreen}
        onNavigate={onNavigate}
      />
      
      {isGenerating && (
        <EnhancedLoadingAnimation
          user={user}
          onLogout={onLogout}
          currentScreen={currentScreen}
          onNavigate={onNavigate}
          animationComponent={AnimationComponent}
          loadingProgress={loadingProgress}
          selectedTags={[
            ...selectedOccasions,
            ...selectedRelationships,
            ...selectedAgeGroups,
            ...selectedPersonality
          ].slice(0, 6)}
        />
      )}

      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-12 relative z-10">
        <div>
          {!showResults ? (
            <div>
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#06402B]/10 rounded-full mb-3 sm:mb-4">
                  <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#06402B]" />
                  <span className="text-xs sm:text-sm font-semibold text-[#06402B]">AI Gift Finder</span>
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-1.5 sm:mb-2">
                  {steps[currentStep].title}
                </h1>
                <p className="text-slate-500 text-sm sm:text-base">
                  {steps[currentStep].subtitle}
                </p>
              </div>

              {/* Guest Tries Counter */}
              {!user && !showResults && (
                <div className="max-w-md mx-auto mb-6">
                  <div className={`px-4 py-3 rounded-xl border-2 ${
                    guestTriesRemaining <= 1 
                      ? 'bg-red-50 border-red-200' 
                      : guestTriesRemaining <= 2
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className={`w-4 h-4 ${
                          guestTriesRemaining <= 1 ? 'text-red-600' : 'text-[#06402B]'
                        }`} />
                        <span className="text-sm font-semibold text-slate-700">
                          {guestTriesRemaining} free {guestTriesRemaining === 1 ? 'search' : 'searches'} remaining
                        </span>
                      </div>
                      <button
                        onClick={() => onNavigate('register')}
                        type="button"
                        className="text-xs font-semibold text-[#06402B] hover:underline"
                      >
                        Sign up for more
                      </button>
                    </div>
                    {guestTriesRemaining <= 1 && (
                      <p className="text-xs text-slate-600 mt-2">
                        {guestTriesRemaining === 0 
                          ? "You've used all free searches. Create a free account for unlimited AI searches!"
                          : "This is your last free search! Create a free account for unlimited AI searches."}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <StepIndicator />

              {(selectedOccasions.length > 0 || selectedRelationships.length > 0) && currentStep > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-8 px-2">
                  {selectedOccasions.map(item => (
                    <span key={item} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-[#06402B] text-white text-xs sm:text-sm font-medium rounded-full">
                      {item}
                    </span>
                  ))}
                  {selectedRelationships.map(item => (
                    <span key={item} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-[#06402B]/80 text-white text-xs sm:text-sm font-medium rounded-full">
                      {item}
                    </span>
                  ))}
                  {selectedAgeGroups.map(item => (
                    <span key={item} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-[#06402B]/60 text-white text-xs sm:text-sm font-medium rounded-full">
                      {item}
                    </span>
                  ))}
                  {selectedPersonality.map(item => (
                    <span key={item} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-[#06402B]/40 text-white text-xs sm:text-sm font-medium rounded-full">
                      {item}
                    </span>
                  ))}
                </div>
              )}

              <div className="mb-8 sm:mb-12">
                {renderStepContent()}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
                {currentStep > 0 && (
                  <button
                    onClick={() => setCurrentStep(prev => prev - 1)}
                    type="button"
                    className="w-full sm:w-auto px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-semibold flex items-center justify-center gap-2 active:scale-95"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                )}
                
                {currentStep < steps.length - 1 ? (
                  <button
                    onClick={() => setCurrentStep(prev => prev + 1)}
                    disabled={!canProceed()}
                    type="button"
                    className="w-full sm:w-auto px-8 py-3 bg-[#06402B] text-white rounded-xl hover:bg-[#053527] transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={handleGenerate}
                    disabled={!selectedOccasions.length && !selectedRelationships.length}
                    type="button"
                    className="w-full sm:w-auto px-8 py-3 bg-[#06402B] text-white rounded-xl hover:bg-[#053527] transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                  >
                    <Sparkles className="w-5 h-5" />
                    Find Perfect Gifts
                  </button>
                )}

                {currentStep === 0 && (
                  <button
                    onClick={handleReset}
                    type="button"
                    className="w-full sm:w-auto px-6 py-3 text-slate-500 hover:text-slate-700 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>
                )}
              </div>

              {(currentStep === 2 || currentStep === 3) && (
                <div className="text-center mt-3 sm:mt-4">
                  <button
                    onClick={() => setCurrentStep(prev => prev + 1)}
                    type="button"
                    className="text-xs sm:text-sm text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Skip this step →
                  </button>
                </div>
              )}

              {!showResults && generationHistory.length > 0 && (
                <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4 sm:mb-6">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#06402B]" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-800">Recent Searches</h3>
                    <span className="text-xs sm:text-sm text-slate-500">({generationHistory.length})</span>
                  </div>

                  <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4 md:overflow-visible md:pb-0"
                    style={{ 
                      scrollbarWidth: 'none', 
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    {generationHistory.slice(0, 6).map((gen) => (
                      <button
                        key={gen.id}
                        onClick={() => loadPreviousGeneration(gen)}
                        type="button"
                        className="flex-shrink-0 w-[75%] sm:w-[60%] md:w-full snap-start text-left p-3 sm:p-4 bg-white border-2 border-slate-200 hover:border-[#06402B] rounded-xl sm:rounded-2xl transition-all hover:shadow-md group active:scale-95"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#06402B] flex-shrink-0 mt-0.5" />
                          <span className="text-[10px] sm:text-xs text-slate-500">
                            {new Date(gen.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {gen.occasions?.slice(0, 2).map((occ, i) => (
                            <span key={i} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 bg-[#06402B]/10 text-[#06402B] rounded-full font-medium">
                              {occ}
                            </span>
                          ))}
                          {gen.relationships?.slice(0, 1).map((rel, i) => (
                            <span key={i} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                              {rel}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs sm:text-sm text-slate-700 line-clamp-2 mb-2">
                          {gen.search_query}
                        </p>
                        <p className="text-[10px] sm:text-xs text-[#06402B] font-semibold group-hover:underline">
                          {gen.results_count} results →
                        </p>
                      </button>
                    ))}
                  </div>

                  {generationHistory.length > 6 && (
                    <div className="text-center mt-4">
                      <button
                        onClick={() => onNavigate('profile')}
                        type="button"
                        className="text-xs sm:text-sm text-[#06402B] hover:underline font-medium"
                      >
                        View all {generationHistory.length} searches in Profile →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div ref={resultsRef}>
              <div className="text-center mb-6 sm:mb-10">
                <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#06402B]/10 rounded-full mb-3 sm:mb-4">
                  <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#06402B]" />
                  <span className="text-xs sm:text-sm font-semibold text-[#06402B]">{results.length} Perfect Matches</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-2 sm:mb-3">
                  Your Personalized Gift Selection
                </h2>
                <p className="text-slate-500 mb-4 sm:mb-6 text-sm sm:text-base px-4">
                  Curated based on: {[...selectedOccasions, ...selectedRelationships].slice(0, 3).join(' • ')}
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
                  {cameFrom === 'profile' && (
                    <button
                      onClick={() => onNavigate('profile')}
                      type="button"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-semibold text-sm sm:text-base active:scale-95"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Profile
                    </button>
                  )}
                  <button
                    onClick={handleReset}
                    type="button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-[#06402B] text-[#06402B] rounded-xl hover:bg-[#06402B]/5 transition-colors font-semibold text-sm sm:text-base active:scale-95"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Start New Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                {results.map(product => (
                  <ProductCard key={product.asin} product={product} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <MainFooter />

      {/* Guest Limit Modal */}
      <GuestLimitModal />

      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default AIFinderScreen;